/**
 * Collection Browser — assets/component-collection-browser.js
 *
 * Performance notes:
 * - Only fetches the FIRST PAGE of the active collection on load.
 *   Other collections are fetched only when the shopper clicks them.
 * - Every (collection + sort + page) response is cached in memory so
 *   switching back to a previously viewed tab/sort is instant.
 * - Sorting is delegated to Shopify's own `sort_by` param on the
 *   products.json endpoint, so it's always correct even with pagination
 *   (no need to download an entire large collection to sort it client-side).
 * - Multiple instances of this section on one page are supported; each
 *   is scoped by its section id.
 */
(function () {
  'use strict';

  function initCollectionBrowser(root) {
    if (!root || root.dataset.cbInitialized === 'true') return;
    root.dataset.cbInitialized = 'true';

    const sectionId = root.dataset.sectionId;
    const dataScript = document.getElementById('CollectionBrowserData-' + sectionId);
    if (!dataScript) return;

    let config;
    try {
      config = JSON.parse(dataScript.textContent);
    } catch (e) {
      console.error('Collection Browser: could not parse config', e);
      return;
    }

    const els = {
      menu: document.getElementById('CollectionMenu-' + sectionId),
      title: document.getElementById('CollectionTitle-' + sectionId),
      description: document.getElementById('CollectionDescription-' + sectionId),
      countText: document.getElementById('ProductCount-' + sectionId),
      grid: document.getElementById('ProductsGrid-' + sectionId),
      noProducts: document.getElementById('NoProducts-' + sectionId),
      sortSelect: document.getElementById('SortSelect-' + sectionId),
      loadMoreBtn: document.getElementById('LoadMore-' + sectionId),
    };

    const settings = root.style; // CSS custom properties carry layout settings
    const imageRatio = root.dataset.imageRatio || 'square';

    const state = {
      collections: config.collections || [],
      current: null,
      sort: 'manual',
      page: 1,
      productsPerPage: config.productsPerPage || 24,
      loading: false,
      cache: new Map(), // key: handle|sort|page -> { products, hasMore }
      currency: config.shopCurrency || 'USD',
      locale: config.locale || 'en',
    };

    if (!state.collections.length) {
      if (els.grid) {
        els.grid.innerHTML = '<p class="collection-browser__empty">Add a collection block in the theme editor to get started.</p>';
      }
      return;
    }

    const currencyFormatter = safeCurrencyFormatter(state.locale, state.currency);

    function safeCurrencyFormatter(locale, currency) {
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency: currency });
      } catch (e) {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
      }
    }

    function cacheKey(handle, sort, page) {
      return handle + '|' + sort + '|' + page;
    }

    function setActiveTab(handle) {
      const items = els.menu.querySelectorAll('.collection-browser__item');
      items.forEach((item) => {
        const btn = item.querySelector('.collection-browser__link');
        item.classList.toggle('is-active', btn && btn.dataset.handle === handle);
        if (btn) btn.setAttribute('aria-selected', btn.dataset.handle === handle ? 'true' : 'false');
      });
    }

    function renderSkeletons(count) {
      const n = Math.min(count, 8);
      let html = '';
      for (let i = 0; i < n; i++) {
        html += '<div class="cb-product cb-skeleton" style="animation-delay:0s;opacity:1;">' +
          '<div class="cb-skeleton__image"></div>' +
          '<div class="cb-skeleton__line"></div>' +
          '<div class="cb-skeleton__line cb-skeleton__line--short"></div>' +
          '</div>';
      }
      els.grid.innerHTML = html;
    }

    function formatPrice(cents) {
      // Shopify's products.json returns price as a decimal string already in shop currency
      return currencyFormatter.format(parseFloat(cents));
    }

    function productCardHTML(product) {
      const image = product.images && product.images[0];
      const secondImage = product.images && product.images[1];
      const variant = product.variants && product.variants[0];
      if (!variant) return '';

      const price = variant.price;
      const comparePrice = variant.compare_at_price;
      const onSale = comparePrice && parseFloat(comparePrice) > parseFloat(price);
      const soldOut = product.variants.every((v) => !v.available);

      const showSecondary = root.dataset.showSecondary === 'true';
      const showBadge = root.dataset.showBadge === 'true';
      const showVendor = root.dataset.showVendor === 'true';

      let imageHTML;
      if (image) {
        imageHTML =
          '<img src="' + escapeAttr(image.src) + '" alt="' + escapeAttr(product.title) +
          '" loading="lazy" class="cb-product__image cb-product__image--primary"' +
          (soldOut ? ' style="opacity:0.4;"' : '') + '>' +
          (secondImage && showSecondary && !soldOut
            ? '<img src="' + escapeAttr(secondImage.src) + '" alt="" loading="lazy" class="cb-product__image cb-product__image--secondary">'
            : '');
      } else {
        imageHTML = '<div class="cb-product__no-image">No image</div>';
      }

      return (
        '<div class="cb-product">' +
          '<a href="/products/' + escapeAttr(product.handle) + '" class="cb-product__link">' +
            '<div class="cb-product__image-wrap" data-ratio="' + imageRatio + '">' +
              imageHTML +
              (onSale && showBadge ? '<span class="cb-product__badge">Sale</span>' : '') +
              (soldOut ? '<div class="cb-product__sold-out"><span class="cb-product__sold-out-text">Sold Out</span></div>' : '') +
            '</div>' +
            '<div class="cb-product__info">' +
              (showVendor && product.vendor ? '<p class="cb-product__vendor">' + escapeHTML(product.vendor) + '</p>' : '') +
              '<h3 class="cb-product__title">' + escapeHTML(product.title) + '</h3>' +
              '<div class="cb-product__price">' +
                (onSale
                  ? '<span class="cb-product__price-sale">' + formatPrice(price) + '</span>' +
                    '<span class="cb-product__price-compare">' + formatPrice(comparePrice) + '</span>'
                  : '<span class="cb-product__price-current">' + formatPrice(price) + '</span>') +
              '</div>' +
            '</div>' +
          '</a>' +
        '</div>'
      );
    }

    function escapeHTML(str) {
      const div = document.createElement('div');
      div.textContent = str == null ? '' : String(str);
      return div.innerHTML;
    }

    function escapeAttr(str) {
      return escapeHTML(str).replace(/"/g, '&quot;');
    }

    async function fetchPage(handle, sort, page) {
      const key = cacheKey(handle, sort, page);
      if (state.cache.has(key)) return state.cache.get(key);

      const params = new URLSearchParams({
        limit: String(state.productsPerPage),
        page: String(page),
      });
      if (sort && sort !== 'manual') {
        params.set('sort_by', sort);
      }

      const url = '/collections/' + encodeURIComponent(handle) + '/products.json?' + params.toString();
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('Collection Browser: request failed (' + res.status + ')');
      const data = await res.json();
      const products = data.products || [];
      const result = {
        products: products,
        hasMore: products.length === state.productsPerPage,
      };
      state.cache.set(key, result);
      return result;
    }

    async function loadCollection(handle, opts) {
      opts = opts || {};
      const resetPage = opts.resetPage !== false;
      if (state.loading) return;
      state.loading = true;

      state.current = handle;
      if (resetPage) state.page = 1;
      setActiveTab(handle);
      if (els.loadMoreBtn) els.loadMoreBtn.disabled = true;

      const meta = state.collections.find((c) => c.handle === handle);
      if (meta) {
        els.title.textContent = meta.title;
        els.description.textContent = meta.description || '';
      }

      if (resetPage) {
        els.grid.classList.remove('is-loading');
        renderSkeletons(state.productsPerPage > 8 ? 8 : state.productsPerPage);
        els.noProducts.hidden = true;
        els.countText.textContent = 'Loading products…';
      } else {
        els.grid.classList.add('is-loading');
      }

      try {
        const result = await fetchPage(handle, state.sort, state.page);

        if (resetPage) {
          els.grid.innerHTML = '';
        }

        if (state.page === 1 && result.products.length === 0) {
          els.grid.innerHTML = '';
          els.noProducts.hidden = false;
          els.countText.textContent = '0 products';
        } else {
          els.noProducts.hidden = true;
          const html = result.products.map(productCardHTML).join('');
          if (resetPage) {
            els.grid.innerHTML = html;
          } else {
            els.grid.insertAdjacentHTML('beforeend', html);
          }
          const loadedCount = els.grid.querySelectorAll('.cb-product:not(.cb-skeleton)').length;
          const totalKnown = meta ? meta.count : null;
          els.countText.textContent = totalKnown
            ? loadedCount + ' of ' + totalKnown + ' product' + (totalKnown !== 1 ? 's' : '')
            : loadedCount + ' product' + (loadedCount !== 1 ? 's' : '');
        }

        if (els.loadMoreBtn) {
          els.loadMoreBtn.hidden = !result.hasMore;
          els.loadMoreBtn.disabled = false;
        }
      } catch (err) {
        console.error(err);
        if (resetPage) {
          els.grid.innerHTML = '<p class="collection-browser__empty">Sorry, something went wrong loading these products.</p>';
        }
      } finally {
        state.loading = false;
        els.grid.classList.remove('is-loading');
      }
    }

    function setupSidebar() {
      els.menu.addEventListener('click', (e) => {
        const btn = e.target.closest('.collection-browser__link');
        if (!btn) return;
        const handle = btn.dataset.handle;
        if (handle === state.current) return;
        loadCollection(handle, { resetPage: true });
      });
    }

    function setupSort() {
      if (!els.sortSelect) return;
      let debounceTimer;
      els.sortSelect.addEventListener('change', (e) => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          state.sort = e.target.value;
          loadCollection(state.current, { resetPage: true });
        }, 120);
      });
    }

    function setupLoadMore() {
      if (!els.loadMoreBtn) return;
      els.loadMoreBtn.addEventListener('click', () => {
        state.page += 1;
        loadCollection(state.current, { resetPage: false });
      });
    }

    // Theme editor: preview a collection immediately when its block is selected
    document.addEventListener('shopify:block:select', (event) => {
      const blockId = event.detail && event.detail.blockId;
      const match = state.collections.find((c) => c.blockId === blockId);
      if (match && match.handle !== state.current) {
        loadCollection(match.handle, { resetPage: true });
      }
    });

    setupSidebar();
    setupSort();
    setupLoadMore();

    const defaultCollection =
      state.collections.find((c) => c.isDefault) || state.collections[0];
    loadCollection(defaultCollection.handle, { resetPage: true });
  }

  function initAll() {
    document.querySelectorAll('.collection-browser').forEach(initCollectionBrowser);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init when the section is added/re-rendered live in the theme editor
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('.collection-browser');
    if (root) {
      root.dataset.cbInitialized = 'false';
      initCollectionBrowser(root);
    }
  });
})();
